import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { ActiveUser } from '../User/ActiveUser';
import { TextLink } from '../Buttons/TextLink';
import designTokens from '@/design-tokens';
import { useAuth } from '@/contexts/AuthProvider';
import { useTempAuthData } from '@/contexts/TempAuthDataContext';
import { useResponsive } from '@/contexts/ResponsiveContext';

export interface TopNavigationBarProps {
  className?: string;
  onToggleDrawer?: () => void;
}

export const TopNavigationBar: React.FC<TopNavigationBarProps> = ({
  className = '',
  onToggleDrawer,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, tenant, signOut } = useAuth();
  const { isCompact } = useResponsive();
  
  // Try to get temp data, but handle case where context might not be available
  let tempData = { company: { companyName: '' }, user: { firstName: '', lastName: '' } };
  try {
    const tempDataContext = useTempAuthData();
    tempData = tempDataContext.tempData;
  } catch (error) {
    // Context not available, use defaults
  }

  const handleSignOut = async () => {
    console.log('TopNavigationBar: Sign out clicked');
    try {
      await signOut();
      console.log('TopNavigationBar: Sign out completed');
      // Navigation is handled by AuthContext's onAuthStateChange listener
    } catch (error) {
      console.error('TopNavigationBar: Sign out error:', error);
    }
  };

  // Check if we're in auth flow
  const isInAuthFlow = location.pathname.startsWith('/auth/');
  
  // Construct display name - use temp data during auth flow
  let userName: string;
  let firstName: string;
  let lastName: string;
  
  if (isInAuthFlow && (tempData.user.firstName || tempData.user.lastName)) {
    // Use temporary data during auth flow
    firstName = tempData.user.firstName;
    lastName = tempData.user.lastName;
    userName = firstName && lastName 
      ? `${firstName} ${lastName}` 
      : firstName || lastName || user?.email || 'User';
  } else {
    // Use actual profile data
    firstName = userProfile?.first_name || '';
    lastName = userProfile?.last_name || '';
    userName = userProfile?.first_name && userProfile?.last_name
      ? `${userProfile.first_name} ${userProfile.last_name}`
      : user?.email || 'User';
  }

  // Get company name - use temp data during auth flow
  const companyName = (isInAuthFlow && tempData.company.companyName) 
    ? tempData.company.companyName 
    : tenant?.name || 'Company';

  return (
    <nav
      className={`fixed top-0 left-0 z-30 ${className}`}
      style={{
        height: designTokens['topnav/height'] || '3rem',
        width: '100vw',
        backgroundColor: designTokens['surface/greyscale/clear'] || '#ffffff',
        borderBottomWidth: designTokens['border/weight/light'] || '0.0625rem',
        borderBottomColor: designTokens['border/color/default'] || '#dee2e6',
        borderBottomStyle: 'solid',
      }}
    >
      <div className="h-full flex items-center justify-between" style={{ paddingLeft: isCompact ? '0.75rem' : '5.5rem', paddingRight: isCompact ? '0.75rem' : '1.5rem' }}>
        <div className="flex items-center" style={{ gap: designTokens['spacing/twenty-four'] || '24px' }}>
          {isCompact && (
            <button
              onClick={onToggleDrawer}
              aria-label="Toggle navigation menu"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                color: designTokens['icon/color/primary'] || '#343a40',
                borderRadius: '0.5rem',
                padding: 0,
              }}
            >
              <Menu size={24} />
            </button>
          )}
          <TextLink onClick={() => window.open('https://www.prophitable.io/updates', '_blank')}>
            Updates
          </TextLink>
          <TextLink onClick={() => window.open('https://www.prophitable.io/help', '_blank')}>
            Help
          </TextLink>
        </div>
        <div className="flex items-center gap-3">
          <ActiveUser
            companyName={companyName}
            userName={userName}
            userImageUrl={userProfile?.profile_image_url ?? undefined}
            firstName={firstName}
            lastName={lastName}
            onProfileClick={() => navigate('/settings/profile')}
            onSettingsClick={() => navigate('/settings')}
            onSignOutClick={handleSignOut}
          />
        </div>
      </div>
    </nav>
  );
};